import type { SpellIssue } from '../types/spell-issue'
import type { SpellOccurrence } from '../types/spell-occurrence'

/**
 * Carries the user's Ignore / Ignore all / Change / Add to Dictionary choices over onto a fresh
 * scan, so rescanning (the text changed, a toggle flipped) doesn't bring back words they already
 * dealt with.
 *
 * Choices that say where they are (`shapeId`, `start`, `end`) are matched by that place: the caller
 * moves them through every edit of the text (`trackResolutionsThroughEdits`), so a choice stays on
 * the instance it was made on, and an instance with no choice at its place is open, such as the
 * same word typed again (SC-14). Only a word ignored with Ignore All (`ignoredAll`) or added to the
 * dictionary stays resolved for instances that appear later.
 *
 * Choices without a place are matched by order instead. Only the choice (`status`, `replacement`)
 * is copied, and where each instance is always comes from the fresh scan. Choices for single
 * instances survive while the word still has the same number of instances. A Change is written
 * into the text (WMPCM-13377), so its instance leaves the scan: when the count moved, the
 * `changed` instances are dropped and the rest are matched again by order. If the count still
 * differs, only a word that was ignored or added everywhere stays resolved.
 */
export function applyResolutions(
  scanned: SpellIssue[],
  resolved: readonly SpellIssue[]
): SpellIssue[] {
  const resolvedById = new Map(resolved.map((issue) => [issue.id, issue]))

  return scanned.map((issue) => {
    const previous = resolvedById.get(issue.id)

    if (!previous) return issue

    // An Ignore All with no instances left still applies to the word typed again.
    if (isPlaced(previous, previous.ignoredAll === true) && isPlaced(issue)) {
      return applyByPlace(issue, previous)
    }

    // Equal counts keep every choice, `changed` included: right after a Change the scan can still
    // read the text from before it was written.
    const choices =
      previous.occurrences.length === issue.occurrences.length
        ? previous.occurrences
        : previous.occurrences.filter((o) => o.status !== 'changed')

    if (choices.length === issue.occurrences.length) {
      return {
        ...issue,
        occurrences: issue.occurrences.map((occurrence, index) =>
          withChoiceOf(occurrence, choices[index])
        ),
      }
    }

    // Nothing left after the changes means nothing to carry: the word is new text.
    const ignoredEverywhere =
      choices.length > 0 && choices.every((o) => o.status === 'ignored' || o.status === 'added')
    // Any Add to Dictionary choice keeps the whole word tied to the dictionary, so it comes back
    // if the word is removed (`reopenRemovedWords`).
    const status = choices.some((o) => o.status === 'added') ? 'added' : 'ignored'

    return ignoredEverywhere
      ? {
          ...issue,
          occurrences: issue.occurrences.map((occurrence) => ({ ...occurrence, status })),
        }
      : issue
  })
}

function withChoiceOf(
  fresh: SpellOccurrence,
  previous: SpellOccurrence | undefined
): SpellOccurrence {
  if (!previous) return fresh

  const { replacement } = previous

  return {
    ...fresh,
    status: previous.status,
    ...(replacement === undefined ? {} : { replacement }),
  }
}

/** Whether every instance says where it is, so choices can be matched by place. */
function isPlaced(issue: SpellIssue, allowEmpty = false): boolean {
  return (
    (allowEmpty || issue.occurrences.length > 0) &&
    issue.occurrences.every(
      (o) => o.shapeId !== undefined && o.start !== undefined && o.end !== undefined
    )
  )
}

function applyByPlace(fresh: SpellIssue, previous: SpellIssue): SpellIssue {
  const choiceAt = (occurrence: SpellOccurrence) =>
    previous.occurrences.find(
      (choice) =>
        choice.status !== 'open' &&
        choice.shapeId === occurrence.shapeId &&
        choice.start === occurrence.start &&
        choice.end === occurrence.end &&
        (choice.onPath === true) === (occurrence.onPath === true)
    )
  // Add to Dictionary ties the whole word to the dictionary, so a later instance is added too.
  const addedToDictionary = previous.occurrences.some((o) => o.status === 'added')

  return {
    ...fresh,
    ...(previous.ignoredAll ? { ignoredAll: true as const } : {}),
    occurrences: fresh.occurrences.map((occurrence) => {
      const choice = choiceAt(occurrence)

      if (choice) return withChoiceOf(occurrence, choice)
      if (addedToDictionary) return { ...occurrence, status: 'added' as const }
      if (previous.ignoredAll) return { ...occurrence, status: 'ignored' as const }

      return occurrence
    }),
  }
}
